// ---------------------------------------------------------------------------
// Seções do painel administrativo.
//
// Cada seção tem uma "chave" que é usada para dar (ou negar) permissão a um
// usuário editor. O administrador geral tem acesso a tudo, sempre.
// Esta é a fonte única usada pelo menu lateral, pelos cards do dashboard e
// pelo seletor de menus na tela de Usuários.
// ---------------------------------------------------------------------------

export interface MenuSection {
  key: string;
  label: string;
  to: string;
  icon: string;
  /** Seções que só o administrador geral pode abrir. */
  adminOnly?: boolean;
}

export const MENU_SECTIONS: MenuSection[] = [
  { key: "produtos", label: "Produtos", to: "/admin/produtos", icon: "ri-archive-line" },
  { key: "empresa", label: "Empresa", to: "/admin/empresa", icon: "ri-building-line" },
  { key: "galeria", label: "Galeria", to: "/admin/galeria", icon: "ri-gallery-line" },
  { key: "videos", label: "Vídeos", to: "/admin/videos", icon: "ri-video-line" },
  { key: "hero", label: "Hero", to: "/admin/hero", icon: "ri-slideshow-line" },
  { key: "faq", label: "FAQ", to: "/admin/faq", icon: "ri-questionnaire-line" },
  { key: "usuarios", label: "Usuários", to: "/admin/usuarios", icon: "ri-team-line", adminOnly: true },
  { key: "paginas", label: "Páginas", to: "/admin/paginas", icon: "ri-file-list-3-line" },
  { key: "seo-global", label: "SEO Global", to: "/admin/seo-global", icon: "ri-global-line" },
  { key: "redirecionamentos", label: "Redirecionamentos", to: "/admin/redirecionamentos", icon: "ri-arrow-left-right-line" },
  { key: "home", label: "Home", to: "/admin/home", icon: "ri-home-line" },
  { key: "menus", label: "Menus", to: "/admin/menus", icon: "ri-menu-line" },
  { key: "aparencia", label: "Aparência", to: "/admin/aparencia", icon: "ri-palette-line" },
  { key: "contatos", label: "Contatos", to: "/admin/contatos", icon: "ri-mail-open-line" },
  { key: "email", label: "E-mail", to: "/admin/email", icon: "ri-mail-send-line" },
  { key: "backup", label: "Backup", to: "/admin/backup", icon: "ri-database-2-line" },
  { key: "analytics", label: "Analytics", to: "/admin/analytics", icon: "ri-line-chart-line" },
  { key: "logs", label: "Logs", to: "/admin/logs", icon: "ri-history-line" },
];

export const ALL_SECTION_KEYS: string[] = MENU_SECTIONS.map((s) => s.key);

/** Chaves que um editor pode receber (exclui as exclusivas do administrador). */
export const EDITABLE_SECTION_KEYS: string[] = MENU_SECTIONS.filter((s) => !s.adminOnly).map((s) => s.key);

/**
 * Uma seção é permitida quando:
 * - é o administrador geral (vê tudo), ou
 * - a seção é "Usuários" (só o admin vê), ou
 * - o usuário não tem lista de permissões guardada (usuários antigos = acesso total), ou
 * - a chave está na lista de permissões.
 */
export function isSectionAllowed(
  role: string,
  permissions: string[] | null | undefined,
  key: string,
): boolean {
  if (key === "usuarios") return role === "admin";
  if (role === "admin") return true;
  if (!permissions) return true;
  return permissions.includes(key);
}

/** Descobre a chave da seção a partir do caminho (ex.: "/admin/produtos").
 * Também reconhece sub-rotas (ex.: "/admin/produtos/novo" -> "produtos"). */
export function sectionKeyForPath(path?: string): string | null {
  if (!path) return null;
  const normalized = path.split("?")[0].replace(/\/+$/, "") || "/";
  const found = MENU_SECTIONS.filter(
    (s) => normalized === s.to || normalized.startsWith(`${s.to}/`),
  ).sort((a, b) => b.to.length - a.to.length)[0];
  return found ? found.key : null;
}